import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';
import logoIcon from '../components/assets/icon.png';
import Footer from '../components/Footer';
import '../App.css';

const MovieDetail = () => {
  const { slug } = useParams();
  const [movie, setMovie] = useState(null);
  const [cast, setCast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCast, setSelectedCast] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const handleMenuClick = () => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    document.body.style.overflow = 'unset';
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const openCastDrawer = (member) => {
    setSelectedCast(member);
    setIsDrawerOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeCastDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedCast(null);
    document.body.style.overflow = 'unset';
  };

  const openVideoModal = () => {
    setIsVideoModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const videoIdMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/);
    return videoIdMatch ? `https://www.youtube.com/embed/${videoIdMatch[1]}?autoplay=1` : url;
  };

  useEffect(() => {
    const fetchMovieAndCast = async () => {
      try {
        const moviesQuery = query(collection(db, 'movies'), where('slug', '==', slug));
        const snapshot = await getDocs(moviesQuery);
        
        if (!snapshot.empty) {
          const movieData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
          setMovie(movieData);

          // Fetch cast members for this movie
          const castQuery = query(
            collection(db, 'movieCast'),
            where('movieId', '==', movieData.id),
            orderBy('createdAt', 'desc')
          );
          const castSnapshot = await getDocs(castQuery);
          const castList = castSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setCast(castList);
        }
      } catch (error) {
        console.error('Error fetching movie:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMovieAndCast();
  }, [slug]);

  if (loading) {
    return (
      <div className="movie-detail-page">
        <LoadingSpinner size="large" message="Loading movie details..." />
      </div>
    );
  }

  if (!movie) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <h1 style={{ fontSize: '48px', color: '#1a365d' }}>Movie Not Found</h1>
        <Link to="/" style={{ marginTop: '20px', color: '#5B9BD5', fontSize: '18px' }}>← Back to Home</Link>
      </div>
    );
  }

  return (
    <div className="movie-detail-page">
      {/* Navigation */}
      <nav className="navbar scrolled">
        <div className="container nav-content">
          <Link to="/" className="logo">
            <img src={logoIcon} alt="Magic Moon Studios" className="logo-icon-img" />
            <span className="logo-text">MAGIC MOON STUDIOS</span>
          </Link>

          <button 
            className="mobile-menu-btn" 
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
              setIsDropdownOpen(false);
              document.body.style.overflow = isMenuOpen ? 'unset' : 'hidden';
            }}
          >
            {isMenuOpen ? '✕' : '☰'}
          </button>

          {isMenuOpen && (
            <div 
              className="mobile-menu-backdrop" 
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(false);
                setIsDropdownOpen(false);
                document.body.style.overflow = 'unset';
              }}
            />
          )}

          <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <li><Link to="/" onClick={handleMenuClick}>Home</Link></li>
            <li><a href="/#about" onClick={handleMenuClick}>About</a></li>
            <li><a href="/#projects" onClick={handleMenuClick}>Projects</a></li>
            <li className={`dropdown ${isDropdownOpen ? 'active' : ''}`}>
              <a href="#services" onClick={toggleDropdown}>Services ▾</a>
              <ul className="dropdown-menu">
                <li><Link to="/film-production" onClick={handleMenuClick}>Film Production</Link></li>
                <li><Link to="/short-movies" onClick={handleMenuClick}>Short Movies</Link></li>
                <li><Link to="/academy" onClick={handleMenuClick}>Academy</Link></li>
                <li><Link to="/ads-making" onClick={handleMenuClick}>Ads Making</Link></li>
                <li><Link to="/digital-content" onClick={handleMenuClick}>Digital Content</Link></li>
                <li><Link to="/music-production" onClick={handleMenuClick}>Music Production</Link></li>
              </ul>
            </li>
            <li><a href="/#team" onClick={handleMenuClick}>Team</a></li>
            <li><a href="/#contact" className="contact-btn" onClick={handleMenuClick}>Contact</a></li>
          </ul>
        </div>
      </nav>

      {/* Movie Detail Content */}
      <section className="movie-detail-section">
        <div className="container">
          <div className="movie-detail-content">
            <div className="movie-poster">
              <img src={movie.imageUrl} alt={movie.title} />
              {movie.status === 'upcoming' && (
                <div className="upcoming-badge">Coming Soon</div>
              )}
              {movie.trailerLink && (
                <div className="play-button-overlay" onClick={openVideoModal}>
                  <div className="play-button">
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <circle cx="30" cy="30" r="29" stroke="white" strokeWidth="2" />
                      <path d="M23 18L23 42L42 30L23 18Z" fill="white" />
                    </svg>
                  </div>
                  <span className="play-text">Watch Trailer</span>
                </div>
              )}
            </div>

            <div className="movie-info">
              <div className="movie-header">
                <h1 className="movie-title">{movie.title}</h1>
                <span className="movie-category">{movie.category}</span>
              </div>

              <div className="movie-meta">
                <div className="meta-item">
                  <span className="meta-label">Release:</span>
                  <span className="meta-value">{movie.releaseDate || movie.year}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Status:</span>
                  <span className={`meta-value status-${movie.status}`}>
                    {movie.status === 'released' ? 'Released' : 'Upcoming'}
                  </span>
                </div>
              </div>

              {movie.trailerLink && (
                <div className="movie-actions">
                  <a 
                    href={movie.trailerLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="trailer-btn"
                  >
                    ▶ Watch {movie.status === 'upcoming' ? 'Trailer' : 'Now'}
                  </a>
                </div>
              )}

              <div className="movie-description">
                <h3>About the Movie</h3>
                <p>
                  Experience the captivating story of <strong>{movie.title}</strong>, a {movie.category.toLowerCase()} masterpiece 
                  from Magic Moon Studios. This production showcases our commitment to creating unforgettable visual stories 
                  that resonate with audiences.
                </p>
              </div>
            </div>
          </div>

          {/* Cast Section - Full Width Below Image */}
          <div className="movie-cast-section" style={{ maxWidth: '1200px', margin: '40px auto 0' }}>
            <h3 className="cast-title">Cast & Crew</h3>
                {cast.length > 0 ? (
                  <div className="cast-grid">
                    {cast.map((member) => (
                      <div 
                        key={member.id} 
                        className="cast-card"
                        onClick={() => openCastDrawer(member)}
                      >
                        <div className="cast-image-wrapper">
                          <img src={member.imageUrl} alt={member.name} className="cast-image" />
                        </div>
                        <div className="cast-info">
                          <h4 className="cast-name">{member.name}</h4>
                          <p className="cast-role">{member.role}</p>
                          {member.bio && (
                            <p className="cast-bio-preview">
                              {member.bio.length > 60 ? `${member.bio.substring(0, 60)}...` : member.bio}
                            </p>
                          )}
                        </div>
                        <div className="cast-card-overlay">
                          <span>View Details →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-cast-message">
                    <div className="no-cast-icon">🎬</div>
                    <h4>Cast Information Coming Soon!</h4>
                    <p>Stay tuned for updates on the talented team behind this production.</p>
                  </div>
                )}
          </div>

          {/* Cast Detail Drawer */}
          {isDrawerOpen && selectedCast && (
            <>
              <div className="cast-drawer-backdrop" onClick={closeCastDrawer}></div>
              <div className="cast-drawer">
                <button className="cast-drawer-close" onClick={closeCastDrawer}>✕</button>
                
                <div className="cast-drawer-content">
                  <div className="cast-drawer-image">
                    <img src={selectedCast.imageUrl} alt={selectedCast.name} />
                  </div>
                  
                  <div className="cast-drawer-info">
                    <h2 className="cast-drawer-name">{selectedCast.name}</h2>
                    <span className="cast-drawer-role">{selectedCast.role}</span>
                    
                    {selectedCast.bio && (
                      <div className="cast-drawer-bio">
                        <h4>Biography</h4>
                        <p>{selectedCast.bio}</p>
                      </div>
                    )}
                    
                    {selectedCast.link && (
                      <a 
                        href={selectedCast.link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="cast-drawer-link"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        Visit Profile
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Video Modal */}
          {isVideoModalOpen && movie.trailerLink && (
            <>
              <div className="video-modal-backdrop" onClick={closeVideoModal}></div>
              <div className="video-modal">
                <button className="video-modal-close" onClick={closeVideoModal}>✕</button>
                <div className="video-modal-content">
                  <iframe
                    src={getYouTubeEmbedUrl(movie.trailerLink)}
                    title={`${movie.title} Trailer`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </>
          )}

          {/* Back Link */}
          <div style={{ maxWidth: '1200px', margin: '30px auto 0', padding: '0 20px' }}>
            <Link to="/" className="back-link">
              ← Back to All Projects
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MovieDetail;
